import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, PawPrint, X } from "lucide-react";
import Logo from "./Logo";

const LINKS = [
  { label: "Services", hash: "#services" },
  { label: "How it works", hash: "#how" },
  { label: "Reviews", hash: "#reviews" },
  { label: "Gallery", hash: "#gallery" },
  { label: "Message Ari", to: "/messages" },
  { label: "Pay", to: "/pay" },
];

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const goTo = (hash) => {
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => document.querySelector(hash)?.scrollIntoView({ behavior: "smooth" }), 120);
    } else {
      document.querySelector(hash)?.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <motion.header
      initial={{ y: -70, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,box-shadow,backdrop-filter] duration-300 ${
        scrolled || menuOpen ? "bg-cream/90 shadow-[0_1px_0_0_rgba(42,42,42,0.07)] backdrop-blur-md" : "bg-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-[1440px] items-center justify-between px-6 py-4 md:px-12">
        <Link to="/" data-testid="nav-logo-link" className="transition-opacity duration-200 hover:opacity-80">
          <Logo />
        </Link>
        <nav className="hidden items-center gap-9 md:flex">
          {LINKS.map((l) =>
            l.to ? (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="font-mono text-[11px] uppercase tracking-[0.22em] text-inkSoft transition-colors duration-200 hover:text-marmalade"
              >
                {l.label}
              </Link>
            ) : (
              <button
                key={l.hash}
                data-testid={`nav-link-${l.hash.slice(1)}`}
                onClick={() => goTo(l.hash)}
                className="font-mono text-[11px] uppercase tracking-[0.22em] text-inkSoft transition-colors duration-200 hover:text-marmalade"
              >
                {l.label}
              </button>
            )
          )}
        </nav>
        <div className="flex items-center gap-3">
          <Link
            to="/book"
            data-testid="nav-book-cta"
            className="inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-marmalade px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.22em] text-white transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-marmalade-deep sm:px-6 sm:py-3"
          >
            <PawPrint className="h-3.5 w-3.5" fill="currentColor" strokeWidth={0} />
            Book a sitter
          </Link>
          <button
            onClick={() => setMenuOpen((v) => !v)}
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            aria-expanded={menuOpen}
            data-testid="nav-mobile-toggle"
            className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-ink/15 text-ink transition-colors duration-200 hover:bg-sand md:hidden"
          >
            {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>
      <AnimatePresence>
        {menuOpen && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            className="overflow-hidden border-t border-line bg-cream/95 backdrop-blur-md md:hidden"
            data-testid="nav-mobile-menu"
            aria-label="Mobile navigation"
          >
            <div className="flex flex-col px-6 pb-6 pt-2">
              {LINKS.map((l) =>
                l.to ? (
                  <Link
                    key={l.to}
                    to={l.to}
                    data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                    onClick={() => setMenuOpen(false)}
                    className="rounded-xl px-2 py-3.5 font-mono text-xs uppercase tracking-[0.22em] text-ink transition-colors duration-200 hover:bg-sand"
                  >
                    {l.label}
                  </Link>
                ) : (
                  <button
                    key={l.hash}
                    data-testid={`nav-mobile-link-${l.hash.slice(1)}`}
                    onClick={() => {
                      goTo(l.hash);
                      setMenuOpen(false);
                    }}
                    className="rounded-xl px-2 py-3.5 text-left font-mono text-xs uppercase tracking-[0.22em] text-ink transition-colors duration-200 hover:bg-sand"
                  >
                    {l.label}
                  </button>
                )
              )}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Navbar;
